import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DashboardAPI, QrAPI } from "../../../services/api";
import { parseNames } from "../../../utils/nameParser";
import { ReactComponent as MagnifyingGlassIcon } from "../../../assets/icons/dashboard/magnifyingglass.svg";
import { ReactComponent as RefreshIcon } from "../../../assets/icons/dashboard/refresh.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/icons/listallbuilding/download.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/setqr/delete.svg";


function extractRouteItems(data) {
  const raw =
    (Array.isArray(data) && data) ||
    data?.items ||
    data?.routes ||
    data?.topRoutes ||
    data?.data?.items ||
    data?.data?.routes ||
    data?.data?.topRoutes ||
    [];

  return Array.isArray(raw) ? raw : [];
}

function normalizeRouteRow(row, index, fallbackStartName, t) {
  const startName =
    row.startName ||
    row.fromName ||
    row.originName ||
    row.qrName ||
    row.startQrName ||
    fallbackStartName ||
    "";
  const rawDestName =
    row.destinationName ||
    row.toName ||
    row.destName ||
    row.name ||
    row.routeName ||
    row.destination?.name ||
    t("admin.qrDetail.unspecifiedDestination");
  const destinationName = parseNames(rawDestName).primary;
  const route =
    row.route ||
    row.label ||
    (startName ? `${startName} → ${destinationName}` : destinationName);

  return {
    id: row.id || row.destinationId || row.toId || `${route}-${index}`,
    startQrId: row.startQrId || row.fromQrId || row.qrId || row.originQrId,
    startName,
    route,
    building: row.building || row.buildingName || row.floorName || "",
    destinationId: row.destinationId || row.toId || row.destination?.id,
    destinationName,
    avgDistanceM:
      row.avgDistanceM ?? row.averageDistanceM ?? row.distanceM ?? row.distance,
    scanCount: Number(row.scanCount ?? row.scans ?? row.count ?? row.total ?? row.totalScans ?? 0),
  };
}

function routeStartsFromQr(row, qr) {
  if (!qr) return false;
  const ids = [row.startQrId, row.fromQrId, row.qrId, row.originQrId]
    .filter((value) => value != null)
    .map(String);
  if (ids.length > 0 && ids.includes(String(qr.id))) return true;

  const startName =
    row.startName || row.fromName || row.originName || row.qrName || row.startQrName || "";
  const routeText = row.route || row.label || "";
  const qrName = String(qr.name || "").trim();
  if (!qrName) return false;

  return (
    String(startName).trim() === qrName ||
    String(routeText).trim().startsWith(qrName)
  );
}

/**
 * หน้ารายละเอียด QR + analytics
 * (ตามรูปที่ 4)
 *  - ซ้าย: ตารางต้นทาง→ปลายทาง พร้อมจำนวน scan
 *  - ขวา: QR card + ปุ่ม download / delete
 */
export default function QrDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [qr, setQr] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [topRouteRows, setTopRouteRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 6;

  useEffect(() => {
    setLoading(true);
    Promise.all([
      QrAPI.get(id),
      QrAPI.analytics(id).catch(() => null),
      DashboardAPI.topRoutesAnalytics({ limit: 200 }).catch(() => null),
    ])
      .then(([q, a, topRoutes]) => {
        setQr(q);
        setAnalytics(a);
        setTopRouteRows(extractRouteItems(topRoutes));
      })
      .catch(() => {
        setQr(null);
        setTopRouteRows([]);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const totalScanCount = Number(
    analytics?.totalScans ?? qr?.scanCount ?? qr?.scans ?? qr?.totalScans ?? 0
  );

  const filteredRoutes = useMemo(() => {
    const rawRows =
      analytics?.byDestination ||
      analytics?.destinations ||
      analytics?.routes ||
      analytics?.items ||
      [];
    const analyticsRows = Array.isArray(rawRows)
      ? rawRows.map((row, index) => normalizeRouteRow(row, index, qr?.name, t))
      : [];
    const dashboardRows = topRouteRows
      .filter((row) => routeStartsFromQr(row, qr))
      .map((row, index) => normalizeRouteRow(row, index, qr?.name, t));
    const rows = analyticsRows.length > 0 ? analyticsRows : dashboardRows;

    if (rows.length === 0 && totalScanCount > 0) {
      rows.push({
        id: "scan-summary",
        route: `${qr?.name || ""} → ${t("admin.qrDetail.unspecifiedDestination")}`,
        building: qr?.floorName || qr?.buildingName || "",
        destinationId: null,
        destinationName: t("admin.qrDetail.unspecifiedDestination"),
        avgDistanceM: null,
        scanCount: totalScanCount,
        isScanOnly: true,
      });
    }

    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter((r) =>
      (r.route || r.destinationName || "").toLowerCase().includes(s) ||
      (r.building || "").toLowerCase().includes(s)
    );
  }, [analytics, qr, search, t, topRouteRows, totalScanCount]);

  const pagedRoutes = filteredRoutes.slice(
    (page - 1) * pageSize,
    page * pageSize
  );
  const totalPages = Math.max(1, Math.ceil(filteredRoutes.length / pageSize));

  const onDownload = () => {
    const a = document.createElement("a");
    a.href = QrAPI.downloadUrl(id, qr?.code);
    a.download = `${qr?.code || "qr"}.png`;
    a.target = "_blank";
    a.click();
  };

  const onDelete = async () => {
    if (!qr) return;
    if (!window.confirm(t("admin.qrViewModal.confirmDelete", { code: qr.code }))) return;
    try {
      await QrAPI.remove(id);
      navigate("/admin/qr");
    } catch (e) {
      alert(e.message || t("admin.qrViewModal.deleteFailed"));
    }
  };

  if (loading) {
    return <div className="p-8 text-slate-400">{t("common.loading")}</div>;
  }
  if (!qr) {
    return (
      <div className="p-8 text-slate-500">
        {t("admin.qrDetail.qrNotFound")}{" "}
        <button
          className="text-blue-600 underline"
          onClick={() => navigate("/admin/qr")}
        >
          {t("common.back")}
        </button>
      </div>
    );
  }

  return (
    <div className="p-1">
      {/* breadcrumb */}
      <button
        onClick={() => navigate("/admin/qr")}
        className="flex items-center gap-1 text-black hover:text-slate-700 text-xl font-semibold mb-3"
      >
        <span className="text-4xl font-light p-1 leading-none">‹</span>
        {t("admin.qrDetail.masterQrTableLink")}
      </button>
      <p className="text-sm text-slate-500 -mt-3 mb-4 p-1">
        {t("admin.qrList.subtitle")}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        {/* LEFT: routes table */}
        <div className="flex min-h-[calc(100vh-180px)] flex-col ">
        <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
          <div className="mb-4 flex h-10 max-w-sm items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 focus-within:border-blue-400">
            <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-slate-500" />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder={t("common.search")}
              className="h-full w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
            />
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-100 shadow-sm">
            <table className="w-full text-left border-collapse min-w-[640px] table-fixed">
              <colgroup>
                <col className="w-[58%]" />
                <col className="w-[21%]" />
                <col className="w-[21%]" />
              </colgroup>
              <thead>
                <tr className="bg-slate-50 text-sm font-base text-slate-500 uppercase tracking-wider border-b border-slate-100">
                  <th className="py-4 px-5">{t("admin.qrDetail.originDestinationCol")}</th>
                  <th className="py-4 px-5 text-center">{t("admin.qrDetail.averageDistanceCol")}</th>
                  <th className="py-4 px-5 text-center">{t("admin.qrDetail.usersCountCol")}</th>
                </tr>
              </thead>
              <tbody>
                {pagedRoutes.length === 0 && (
                  <tr>
                    <td colSpan={3} className="py-12 text-center font-bold text-slate-400">
                      {t("admin.qrDetail.noScanData")}
                    </td>
                  </tr>
                )}
                {pagedRoutes.map((r, i) => (
                  <tr
                    key={`${r.id || r.destinationId}-${i}`}
                    className="hover:bg-slate-50/50 transition-colors"
                  >
                    <td className="py-4 px-8">
                      <div className="font-semibold text-slate-900 text-base">
                        <span>{r.route || qr.name}</span>
                        <span className={r.route ? "hidden" : "text-blue-500"}>→</span>
                        <span>
                          {r.route ? "" : (r.destinationName || t("admin.qrDetail.unspecifiedDestination"))}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 font-base mt-1">
                        {r.building || qr.floorName || qr.buildingName || "-"}
                      </div>
                    </td>
                    <td className="py-4 px-8 text-center font-semibold text-green-700 text-base">
                      {r.avgDistanceM != null
                        ? `${Math.round(r.avgDistanceM)} m`
                        : "-"}
                    </td>
                    <td className="py-4 px-8 text-center font-semibold text-blue-700 text-base">
                      {t("admin.qrDetail.times", { count: r.scanCount })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-9 h-9 hover:bg-slate-50 font-black text-slate-700 disabled:opacity-50 disabled:hover:bg-transparent"
              >
                ‹
              </button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i + 1)}
                  className={`rounded-xl px-2 py-1.5 text-sm font-semibold border ${page === i + 1
                    ? "border-blue-600 bg-blue-600 text-white font-semibold"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                >
                  {String(i + 1).padStart(2, "0")}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-9 h-9  hover:bg-slate-50 font-black text-slate-700 disabled:opacity-50 disabled:hover:bg-transparent"
              >
                ›
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-auto text-left border border-slate-200 rounded-lg p-4 bg-white text-xs text-slate-500">
          {t("admin.feedbackDetail.copyright")}
        </div>
        </div>

        {/* RIGHT: QR card */}
        <div className="min-h-[calc(100vh-180px)] bg-white rounded-lg border border-slate-200 p-5">
          <div className="flex items-center justify-between mt-3">
            <div className="font-semibold text-base mt-2 text-slate-700">{t("admin.qrViewModal.autoGenerated")}</div>
            <div className="font-base text-sm mt-2 text-blue-700 inline-flex items-center gap-1.5">
              <RefreshIcon className="w-3 h-3 text-blue-700" />
              {t("admin.qrViewModal.newGenerated")}
            </div>
            {/* placeholder ปุ่มสร้างใหม่ — backend ต้อง support regenerate ถ้าจะใช้ */}
          </div>

          <div className="flex justify-center my-4">
            <div className="border-y-4 border-yellow-500 py-3 px-6">
              <img
                src={QrAPI.imageUrl(qr.id, 512, qr.code)}
                alt={qr.code}
                className="w-36 h-36"
              />
            </div>
          </div>

          <div className="space-y-2 text-sm">
            <Row label={t("admin.qrDetail.qrCodeNameLabel")} value={qr.name} />
            <Row label={t("admin.qrDetail.floorLabel")} value={qr.floorName || "-"} />
            <Row
              label={t("admin.qrDetail.mapCoordinatesLabel")}
              value={`X: ${Number(qr.x).toFixed(1)}  Y: ${Number(qr.y).toFixed(1)}`}
            />
            <Row label={t("admin.qrDetail.createdByLabel")} value={qr.createdBy?.email || "-"} />
            {/* <Row
              label={t("admin.qrDetail.totalScans")}
              value={t("admin.qrDetail.times", { count: totalScanCount })}
            /> */}
          </div>

          <button
            onClick={onDownload}
            className="mt-4 w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-base flex items-center justify-center gap-2"
          >
            <DownloadIcon className="w-4 h-4 text-white" />
            {t("admin.qrViewModal.downloadQr")}
          </button>
          <button
            onClick={onDelete}
            className="mt-2 w-full h-11 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 border border-red-100 font-base flex items-center justify-center gap-2"
          >
            <DeleteIcon className="w-4 h-4 text-red-600" />
            {t("admin.qrViewModal.deleteQr")}
          </button>
        </div>
      </div>
    </div>
  );
}


function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1 border-b border-slate-50">
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-slate-800 text-right">{value}</span>
    </div>
  );
}
